// Direct Manager Discrepancy Audit — Run detail page (ADR-010 Phase 6).
//
// Page 2 of the list/detail pattern. Shows one run's summary card and the
// (filterable, infinitely-loading) discrepancies for that run.
//
// Scroll position + filters are preserved across navigation to the employee
// detail page (mirrors the bench-report pattern).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import { FaArrowLeft } from 'react-icons/fa';
import { CircularProgress } from '@mui/material';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  MainSection,
  BackButton,
} from '../styles';
import {
  useRunDetail,
  useRunDiscrepancies,
} from '../../../hooks/useDirectManagerDiscrepancies';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import SummaryCard from './SummaryCard';
import DiscrepanciesFilterBar from './DiscrepanciesFilterBar';
import DiscrepancyTable from './DiscrepancyTable';
import SourceRecordDialog from './SourceRecordDialog';
import { LoadMoreButton } from './styles';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { DEFAULT_FILTERS, buildViewFilters } from './discrepancyViewFilters';

const PAGE_LIMIT = 50;

const RunDetailPage = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const { runId } = useParams();
  const ability = useAbility();
  // ADR-131: stored feed record viewer
  const canReadSourceRecord = ability.can(ACTIONS.READ, SUBJECTS.DIRECT_MANAGER_DISCREPANCY_SOURCE_RECORD);
  const [sourceRecordRow, setSourceRecordRow] = useState(null);

  // Hydrate filters + scroll position from history state when returning from
  // the employee detail page. `runDetailState` is the bag we pass when we
  // navigate away (see handleNavigateToEmployee below).
  const restored = location.state?.runDetailState;

  const { run, isLoading: runLoading, error: runError } = useRunDetail(runId);

  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...(restored?.filters || {}),
  }));

  // Filters are part of the hook's filterKey — when any change, the hook
  // resets the accumulated items and refetches from the top.
  const apiFilters = useMemo(() => ({
    runId,
    ...buildViewFilters(filters),
    employeeId: filters.employee?.id || undefined,
    limit: PAGE_LIMIT,
  }), [runId, filters]);

  const {
    items: rows,
    pageInfo,
    isLoading: rowsLoading,
    isLoadingMore,
    error: rowsError,
    loadMore,
  } = useRunDiscrepancies(apiFilters);

  // Scroll restoration: capture once when the rows finish loading on return.
  const mainSectionRef = useRef(null);
  const scrollRestoredRef = useRef(false);

  useEffect(() => {
    if (
      !rowsLoading
      && restored?.scrollTop
      && !scrollRestoredRef.current
      && mainSectionRef.current
    ) {
      mainSectionRef.current.scrollTop = restored.scrollTop;
      scrollRestoredRef.current = true;
    }
  }, [rowsLoading, restored]);

  const handleFilterChange = (next) => {
    setFilters((prev) => ({ ...prev, ...next }));
  };

  const handleNavigateToEmployee = (employee) => {
    if (!employee?.id) {
      return;
    }
    // ADR-109 — auto open (Edit when the user manages the individual)
    openIndividual(employee.id, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.DIRECT_MANAGER_DISCREPANCIES,
      returnTo: location.pathname + location.search,
      returnLabel: 'Run Detail',
      // Preserve everything needed to reconstruct this view exactly.
      state: {
        runDetailState: {
          filters,
          scrollTop: mainSectionRef.current?.scrollTop || 0,
        },
      },
    });
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports/direct-manager-discrepancies/runs')}>
            <FaArrowLeft /> Back to Runs
          </BackButton>
          <Title>Run Detail</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection ref={mainSectionRef}>
          {runLoading && <LoadingSpinner />}
          {!runLoading && runError && (
            <EmptyState
              title="Failed to load run"
              description={runError?.message || 'Run not found or unreachable.'}
            />
          )}
          {!runLoading && !runError && !run && (
            <EmptyState
              title="Run not found"
              description="This run id does not exist or has been removed."
            />
          )}
          {!runLoading && !runError && run && (
            <>
              <SummaryCard run={run} />

              <DiscrepanciesFilterBar
                matchFilter={filters.matchFilter}
                mismatchReason={filters.mismatchReason}
                mappingStatus={filters.mappingStatus}
                radarOnlyReason={filters.radarOnlyReason}
                employee={filters.employee}
                onChange={handleFilterChange}
              />

              <ResultsCount
                count={pageInfo?.totalCount || 0}
                singular="individual"
                plural="individuals"
              />

              {rowsLoading && <LoadingSpinner />}
              {!rowsLoading && rowsError && (
                <EmptyState
                  title="Failed to load discrepancies"
                  description={rowsError?.message || 'Try refreshing.'}
                />
              )}
              {!rowsLoading && !rowsError && rows.length === 0 && (
                <EmptyState
                  title="No discrepancies match these filters"
                  description="Try widening the match filter or removing reason/status filters."
                />
              )}
              {!rowsLoading && !rowsError && rows.length > 0 && (
                <>
                  <DiscrepancyTable
                    rows={rows}
                    onNavigateToEmployee={handleNavigateToEmployee}
                    mode={filters.matchFilter === 'radarOnly' ? 'radarOnly' : 'compare'}
                    onViewSourceRecord={canReadSourceRecord ? setSourceRecordRow : undefined}
                  />
                  {pageInfo?.hasNextPage && (
                    <LoadMoreButton onClick={loadMore} disabled={isLoadingMore}>
                      {isLoadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      Load More
                    </LoadMoreButton>
                  )}
                </>
              )}
            </>
          )}
        </MainSection>
      </Content>

      <SourceRecordDialog row={sourceRecordRow} onClose={() => setSourceRecordRow(null)} />
    </Container>
  );
};

export default RunDetailPage;
